class FooterTaglineFit extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {number | null} */
  #fitFrame = null;

  #reflowHandler = () => {
    this.refit();
  };

  connectedCallback() {
    this.#resizeObserver = new ResizeObserver(this.#reflowHandler);
    this.#resizeObserver.observe(this);

    this.#mutationObserver = new MutationObserver(this.#reflowHandler);
    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    window.addEventListener('resize', this.#reflowHandler);
    window.addEventListener('weglot:ready', this.#reflowHandler);

    const weglot = window['Weglot'];
    weglot?.on?.('languageChanged', this.#reflowHandler);

    this.refit();
    requestAnimationFrame(() => this.refit());
    requestAnimationFrame(() => requestAnimationFrame(() => this.refit()));
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    window.removeEventListener('resize', this.#reflowHandler);
    window.removeEventListener('weglot:ready', this.#reflowHandler);

    const weglot = window['Weglot'];
    weglot?.off?.('languageChanged', this.#reflowHandler);

    if (this.#fitFrame) {
      cancelAnimationFrame(this.#fitFrame);
    }
  }

  refit() {
    if (this.#fitFrame) {
      cancelAnimationFrame(this.#fitFrame);
    }

    this.#fitFrame = requestAnimationFrame(() => {
      this.#fitFrame = null;
      this.#fit();
    });
  }

  #fit() {
    const fitMaxWidth = Number.parseInt(this.dataset.fitMaxWidth ?? '1019', 10);
    const minFontSize = Number.parseFloat(this.dataset.minFontSize ?? '14');
    const dataMaxFontSize = Number.parseFloat(this.dataset.maxFontSize ?? '46');
    const isCompact = !window.matchMedia(`(min-width: ${fitMaxWidth + 1}px)`).matches;

    const textEl = this.querySelector('[data-footer-tagline-text]');

    if (!(textEl instanceof HTMLElement) || !this.textContent?.trim()) {
      return;
    }

    const containerWidth = this.getBoundingClientRect().width;

    if (containerWidth <= 0) {
      return;
    }

    // Compact: scale from data-max-font-size. Desktop: start from CSS clamp() size, then shrink if the (longer) translation overflows.
    this.style.fontSize = '';

    let maxFontSize = dataMaxFontSize;

    if (!isCompact) {
      const cssFontSize = Number.parseFloat(getComputedStyle(this).fontSize);

      if (Number.isFinite(cssFontSize) && cssFontSize > 0) {
        maxFontSize = cssFontSize;
      }
    }

    this.style.fontSize = `${maxFontSize}px`;
    let textWidth = textEl.getBoundingClientRect().width;

    if (textWidth <= 0) {
      return;
    }

    if (textWidth <= containerWidth) {
      if (!isCompact) {
        // Keep CSS clamp() when the line already fits (EN/short locales).
        this.style.fontSize = '';
      }

      return;
    }

    let fontSize = (maxFontSize * containerWidth) / textWidth;
    fontSize = Math.max(minFontSize, fontSize);
    this.style.fontSize = `${Math.round(fontSize * 100) / 100}px`;

    textWidth = textEl.getBoundingClientRect().width;

    if (textWidth <= 0) {
      return;
    }

    const refinedSize = (fontSize * containerWidth) / textWidth - 0.15;
    fontSize = Math.max(minFontSize, refinedSize);
    this.style.fontSize = `${Math.round(fontSize * 100) / 100}px`;
  }
}

const initFooterTaglineFit = () => {
  if (!customElements.get('footer-tagline-fit')) {
    customElements.define('footer-tagline-fit', FooterTaglineFit);
  }

  document.querySelectorAll('footer-tagline-fit').forEach((element) => {
    if (element instanceof FooterTaglineFit) {
      element.refit();
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFooterTaglineFit, { once: true });
} else {
  initFooterTaglineFit();
}

document.fonts?.ready?.then(() => {
  document.querySelectorAll('footer-tagline-fit').forEach((element) => {
    if (element instanceof FooterTaglineFit) {
      element.refit();
    }
  });
});
